(() => {
  const roots = document.querySelectorAll('[data-topshelf-wishlist-page]');
  if (!roots.length) return;

  const getVariantId = (value) => String(value || '').split('/').pop();
  const escapeHtml = (value) =>
    String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const parseJson = async (response) => {
    const text = await response.text();
    if (!text) return null;

    try {
      return JSON.parse(text);
    } catch (_) {
      return {message: text};
    }
  };

  roots.forEach((root) => {
    const addButton = root.querySelector('[data-wishlist-add-selected]');
    const removeSelectedButton = root.querySelector('[data-wishlist-remove-selected]');
    const selectedCount = root.querySelector('[data-wishlist-selected-count]');
    const wishlistCount = root.querySelector('[data-wishlist-count]');
    const emptyState = root.querySelector('[data-wishlist-empty]');
    const grid = root.querySelector('[data-wishlist-grid]');
    const toolbar = root.querySelector('[data-wishlist-toolbar]');
    const loadingState = root.querySelector('[data-wishlist-loading]');
    const endpoint = root.dataset.wishlistEndpoint;
    const token = root.dataset.wishlistToken;
    const customerId = root.dataset.customerId;
    const customerEmail = root.dataset.customerEmail;
    const customerFirstName = root.dataset.customerFirstName;
    const customerLastName = root.dataset.customerLastName;

    const getCards = () => Array.from(root.querySelectorAll('[data-wishlist-card]'));
    const getCheckedBoxes = () =>
      Array.from(root.querySelectorAll('[data-wishlist-select]:checked'));
    const getCheckedAvailableBoxes = () =>
      getCheckedBoxes().filter((checkbox) => !checkbox.hasAttribute('data-wishlist-unavailable'));

    const updateHeaderWishlistCount = (count) => {
      document.querySelectorAll('.kith-menu-wishlist-bubble').forEach((bubble) => {
        bubble.textContent = count > 99 ? '99+' : String(count);
        bubble.classList.toggle('visually-hidden', count === 0);
      });

      try {
        localStorage.setItem('topshelf:wishlist-valid-count', String(count));
      } catch (_) {}
      document.documentElement.style.removeProperty('--topshelf-wishlist-cached-count');
      document.documentElement.dataset.topshelfWishlistReady = 'true';
      window.__topshelfWishlistCount = count;
    };

    const updateSelection = () => {
      const checked = getCheckedBoxes();
      const checkedAvailable = getCheckedAvailableBoxes();

      if (addButton) {
        addButton.disabled = checkedAvailable.length === 0;
      }

      if (removeSelectedButton) {
        removeSelectedButton.disabled = checked.length === 0;
      }

      if (selectedCount) {
        selectedCount.textContent = checked.length
          ? `${checked.length} selected`
          : 'Select items to remove or add to cart';
      }
    };

    const updateWishlistCount = () => {
      if (!wishlistCount) return;

      const count = getCards().length;
      wishlistCount.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      updateHeaderWishlistCount(count);
    };

    const updateEmptyState = () => {
      const hasCards = getCards().length > 0;
      if (loadingState) loadingState.hidden = true;
      if (emptyState) emptyState.hidden = hasCards;
      if (grid) grid.hidden = !hasCards;
      if (toolbar) toolbar.hidden = !hasCards;
      updateWishlistCount();
      updateSelection();
    };

    const fetchWishlistVariants = async (variantIds) => {
      if (!variantIds.length) return [];

      const response = await fetch('/api/2026-01/graphql.json', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            query TopShelfWishlistVariants($ids: [ID!]!) {
              nodes(ids: $ids) {
                ... on ProductVariant {
                  id
                  title
                  availableForSale
                  price {
                    amount
                    currencyCode
                  }
                  image {
                    url
                    altText
                  }
                  product {
                    id
                    title
                    handle
                    featuredImage {
                      url
                      altText
                    }
                  }
                }
              }
            }
          `,
          variables: { ids: variantIds },
        }),
      });
      const data = await parseJson(response);

      if (!response.ok || data?.errors) {
        throw new Error(data?.errors?.[0]?.message || 'Unable to load wishlist products.');
      }

      return (data?.data?.nodes || []).filter(Boolean);
    };

    const createLiveCard = (variant) => {
      const variantId = getVariantId(variant.id);
      const productId = getVariantId(variant.product?.id);
      const productTitle = variant.product?.title || 'Wishlist item';
      const variantTitle = variant.title && variant.title !== 'Default Title' ? variant.title : '';
      const productUrl = `/products/${encodeURIComponent(variant.product?.handle || '')}?variant=${encodeURIComponent(variantId)}`;
      const image = variant.image || variant.product?.featuredImage;
      const imageUrl = image?.url
        ? `${image.url}${image.url.includes('?') ? '&' : '?'}width=500`
        : '';
      const currency = variant.price?.currencyCode || 'USD';
      const price = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
      }).format(Number(variant.price?.amount || 0));
      const unavailable = !variant.availableForSale;
      const card = document.createElement('li');

      card.className = 'product-grid__item topshelf-wishlist-page__item';
      card.dataset.wishlistCard = '';
      card.dataset.variantId = variantId;
      card.dataset.variantGid = variant.id;
      card.dataset.productId = productId;
      card.innerHTML = `
        <div class="topshelf-wishlist-page__card-actions">
          <label class="topshelf-wishlist-page__select${unavailable ? ' topshelf-wishlist-page__select--unavailable' : ''}">
            <input
              type="checkbox"
              value="${escapeHtml(variant.id)}"
              data-wishlist-select
              ${unavailable ? 'data-wishlist-unavailable' : ''}
            >
            <span>Select</span>
          </label>
        </div>
        ${unavailable ? '<span class="topshelf-wishlist-page__availability">Unavailable</span>' : ''}
        <a class="topshelf-wishlist-live-card" href="${escapeHtml(productUrl)}">
          <span class="topshelf-wishlist-live-card__media">
            ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(image?.altText || productTitle)}" width="500" height="500" loading="lazy">` : ''}
          </span>
          <span class="topshelf-wishlist-live-card__content">
            <span class="topshelf-wishlist-live-card__title">${escapeHtml(productTitle)}</span>
            ${variantTitle ? `<span class="topshelf-wishlist-live-card__variant">${escapeHtml(variantTitle)}</span>` : ''}
            <span class="topshelf-wishlist-live-card__price">${escapeHtml(price)}</span>
          </span>
        </a>
      `;

      return card;
    };

    const syncLiveWishlist = async () => {
      if (!endpoint || !token || !customerId || !grid) {
        updateEmptyState();
        return;
      }

      try {
        const response = await fetch(`${endpoint}?customer_id=${encodeURIComponent(customerId)}`, {
          headers: {
            Accept: 'application/json',
            'X-Wishlist-Token': token,
          },
        });
        const data = await parseJson(response);

        if (!response.ok || data?.ok === false) {
          throw new Error(data?.message || 'Unable to load your wishlist.');
        }

        const variantIds = [...new Set(data?.wishlist?.variant_ids || [])];
        const liveIdSet = new Set(variantIds);
        const currentCards = new Map(
          getCards().map((card) => [card.dataset.variantGid, card]),
        );

        currentCards.forEach((card, variantId) => {
          if (!liveIdSet.has(variantId)) {
            card.remove();
            currentCards.delete(variantId);
          }
        });

        const missingIds = variantIds.filter((variantId) => !currentCards.has(variantId));
        const variants = await fetchWishlistVariants(missingIds);
        const variantsById = new Map(variants.map((variant) => [variant.id, variant]));

        variantIds.forEach((variantId) => {
          let card = currentCards.get(variantId);
          if (!card && variantsById.has(variantId)) {
            card = createLiveCard(variantsById.get(variantId));
          }
          if (card) grid.append(card);
        });

        updateEmptyState();
      } catch (error) {
        updateEmptyState();
        root.dispatchEvent(
          new CustomEvent('topshelf:wishlist-error', {
            detail: { message: error instanceof Error ? error.message : 'Unable to load your wishlist.' },
          }),
        );
      }
    };

    const removeWishlistItem = async (variantId) => {
      if (!endpoint || !token || !customerId) return;

      const response = await fetch(`${endpoint}/items`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Wishlist-Token': token,
        },
        body: JSON.stringify({
          customer: {
            id: customerId,
            email: customerEmail,
            first_name: customerFirstName,
            last_name: customerLastName,
          },
          variant_id: variantId,
        }),
      });
      const data = await parseJson(response);

      if (!response.ok || data?.ok === false) {
        throw new Error(data?.message || 'Unable to remove selected items.');
      }
    };

    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-wishlist-select]')) updateSelection();
    });

    removeSelectedButton?.addEventListener('click', async () => {
      const checkedBoxes = getCheckedBoxes();
      if (!checkedBoxes.length) return;

      removeSelectedButton.disabled = true;
      removeSelectedButton.setAttribute('aria-busy', 'true');
      if (addButton) addButton.disabled = true;

      try {
        await Promise.all(
          checkedBoxes.map((checkbox) => removeWishlistItem(checkbox.value)),
        );

        checkedBoxes.forEach((checkbox) => {
          checkbox.closest('[data-wishlist-card]')?.remove();
        });

        updateEmptyState();
      } catch (error) {
        root.dispatchEvent(
          new CustomEvent('topshelf:wishlist-error', {
            detail: {message: error instanceof Error ? error.message : 'Unable to remove selected items.'},
          }),
        );
        updateSelection();
      } finally {
        removeSelectedButton.removeAttribute('aria-busy');
      }
    });

    addButton?.addEventListener('click', () => {
      const variantIds = getCheckedAvailableBoxes()
        .map((checkbox) => getVariantId(checkbox.value))
        .filter(Boolean);

      if (!variantIds.length) return;

      window.location.href = `/cart/${variantIds.map((id) => `${id}:1`).join(',')}`;
    });

    root.addEventListener('topshelf:wishlist-error', (event) => {
      const message = root.querySelector('[data-wishlist-error]');
      if (!message) return;

      message.textContent = event.detail?.message || 'Something went wrong. Please try again.';
      message.hidden = false;
    });

    syncLiveWishlist();
  });
})();
